import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocationsStore } from './useLocationsStore';
import { shallow } from 'zustand/shallow';
import { useUserLocationsQuery } from './useUserLocationsQuery';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';

// Define the maximum number of recent locations to keep
export const MAX_RECENT_LOCATIONS = 8;

interface Context {
  recentLocations: HoneLocationUser[];
  setRecentLocations: React.Dispatch<React.SetStateAction<HoneLocationUser[]>>;
  showLocationNotFoundModal: boolean;
  setShowLocationNotFoundModal: (show: boolean) => void;
  removedLocationId: string | null;
  removeLocation: (locationId: string) => void;
}

const RecentLocationsContext = createContext<Context>({
  recentLocations: [],
  setRecentLocations: () => {},
  showLocationNotFoundModal: false,
  setShowLocationNotFoundModal: () => {},
  removedLocationId: null,
  removeLocation: () => {},
});

export const useRecentLocations = () => useContext(RecentLocationsContext);

interface RecentLocationsProviderProps {
  children: React.ReactNode;
}

const RecentLocationsProvider = ({ children }: RecentLocationsProviderProps) => {
  const { data: locations } = useUserLocationsQuery();
  const castedLocations = locations as HoneLocationUser[];
  const { activeLocation } = useLocationsStore(state => ({ activeLocation: state.currentLocationId }), shallow);
  const [recentLocations, setRecentLocations] = useState<HoneLocationUser[]>([]);
  const [removedLocationId, setRemovedLocationId] = useState<string | null>(null);

  const [showLocationNotFoundModal, setShowLocationNotFoundModal] = useState(false);

  /**
   * Syncs recent locations with available user locations
   * Removes locations that no longer exist in the user's available locations
   */
  const syncRecentLocationsWithAvailable = useCallback(
    (storedLocations: HoneLocationUser[]): HoneLocationUser[] => {
      if (!castedLocations || castedLocations.length === 0) {
        return storedLocations;
      }

      const availableLocationIds = new Set(castedLocations.map(loc => loc.locationId));
      return storedLocations.filter(loc => availableLocationIds.has(loc.locationId));
    },
    [castedLocations]
  );

  useEffect(() => {
    const storedRecentLocations = localStorage.getItem('recentLocations');
    if (storedRecentLocations && locations) {
      try {
        const parsed = JSON.parse(storedRecentLocations);
        const syncedLocations = syncRecentLocationsWithAvailable(parsed);
        const active = syncedLocations.findIndex((loc: HoneLocationUser) => loc.locationId === activeLocation) > -1;
        const location = castedLocations.find(loc => loc.locationId === activeLocation);
        if (location) {
          setRecentLocations(
            active ? syncedLocations : [location, ...syncedLocations.slice(0, MAX_RECENT_LOCATIONS - 1)]
          );
        } else {
          setRecentLocations(syncedLocations);
        }
      } catch (error) {
        // If JSON parsing fails, fall back to default locations
        if (castedLocations && castedLocations.length > 0) {
          setRecentLocations(castedLocations.slice(0, MAX_RECENT_LOCATIONS));
        }
      }
    } else if (castedLocations && castedLocations.length > 0) {
      setRecentLocations(castedLocations.slice(0, MAX_RECENT_LOCATIONS));
    }
  }, [locations, activeLocation, syncRecentLocationsWithAvailable, castedLocations]);

  /**
   * Saves recent locations to localStorage with error handling for quota exceeded scenarios
   */
  const saveToLocalStorage = useCallback((locations: HoneLocationUser[]) => {
    if (locations.length > 0) {
      try {
        localStorage.setItem('recentLocations', JSON.stringify(locations.slice(0, MAX_RECENT_LOCATIONS)));
      } catch (error) {
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          try {
            localStorage.removeItem('recentLocations');
            localStorage.setItem('recentLocations', JSON.stringify(locations.slice(0, MAX_RECENT_LOCATIONS)));
          } catch (retryError) {
            console.error('Failed to save recent locations to localStorage:', retryError);
            Sentry.captureException(retryError);
          }
        } else {
          console.error('Failed to save recent locations to localStorage:', error);
          Sentry.captureException(error);
        }
      }
    }
  }, []);

  useEffect(() => {
    saveToLocalStorage(recentLocations);
  }, [recentLocations, saveToLocalStorage]);

  /**
   * Removes a location from recent locations and localStorage
   * Shows the LocationNotFound modal when a location is removed
   * Tracks the ID of the removed location for display in modal
   */
  const removeLocation = useCallback((locationId: string) => {
    setRecentLocations(prevRecentLocations => prevRecentLocations.filter(loc => loc.locationId !== locationId));

    const currentLocationId = localStorage.getItem('hone:locationId');
    if (currentLocationId === locationId) {
      localStorage.removeItem('hone:locationId');
    }

    setRemovedLocationId(locationId);

    requestAnimationFrame(() => {
      setTimeout(() => {
        setShowLocationNotFoundModal(true);
      }, 150);
    });
  }, []);

  return (
    <RecentLocationsContext.Provider
      value={{
        recentLocations,
        setRecentLocations,
        showLocationNotFoundModal,
        setShowLocationNotFoundModal,
        removedLocationId,
        removeLocation,
      }}
    >
      {children}
    </RecentLocationsContext.Provider>
  );
};

export default RecentLocationsProvider;
